import { Injectable } from '@angular/core';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { from, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { Logger } from '../logger';
import { AppSettingsService } from '../settings';
import { TranslationApiService } from './translation-api/translation-api.service';

/**
 * @description
 * Service for managing the app i18n
 */
@Injectable()
export class GlobalizationService {
  constructor(
    private platform: Platform,
    private translate: TranslateService,
    private appSettings: AppSettingsService,
    private device: Device,
    private translationApi: TranslationApiService
  ) {}

  /**
   * Initialize translation service with a default language
   * @param lang Default language of the application
   */
  init(lang: string): Observable<any> {
    this.translate.setDefaultLang(lang);

    const isNativeApp: boolean = this.platform.is('cordova') && this.device.platform !== 'browser';
    if (isNativeApp) {
      this.platform.resume.subscribe(() => this.update().subscribe());
    }

    return this.update();
  }

  private update(): Observable<any> {
    return this.appSettings.language().pipe(concatMap(lang => from(this.setLanguage(lang))));
  }

  setLanguage(lang: string): Promise<any> {
    moment.locale(lang);
    this.translate.use(lang);

    document.querySelector('html').lang = lang;

    return this.updateTranslation(lang);
  }

  updateTranslation(lang: string = this.translate.currentLang || this.translate.defaultLang): Promise<any> {
    return this.getNewTranslation(lang).then(translations => {
      Logger.debug(`Save new content for ${lang}`, 'Globalization');

      this.lowerKeys(translations);
      this.translate.setTranslation(lang, translations, true);

      return translations;
    });
  }

  private getNewTranslation(lang: string): Promise<any> {
    return this.translationApi.getTranslations(lang).toPromise();
  }

  private lowerKeys(translations: any) {
    const keys = Object.keys(translations);
    keys.forEach(key => {
      const value = translations[key];
      delete translations[key];
      translations[key.toLowerCase()] = value;
      if (typeof translations[key.toLowerCase()] === 'object') {
        this.lowerKeys(translations[key.toLowerCase()]);
      }
    });
  }
}
